import { post, getGraphToken, fetcher, patch, del } from '../../../services/api';
import { getAccessToken } from '../../../services/authService';
import type { ChatMessage, ChatSession } from '../types/domain';
import type {
  NavChatStreamRequest,
  NavResumeStreamRequest,
  NavStreamEvent,
  NavStreamEventHandlers,
} from '../types/api';
import type { HandlerType } from '../types/handlerResults';
import { environment } from '../../../config/environment';

interface ApiThread {
  id: string;
  langgraph_thread_id: string;
  title: string | null;
  title_status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ApiThreadMessage {
  id: string | null;
  role: 'user' | 'assistant';
  content: string;
}

// Simple storage for sessions — capped to prevent unbounded memory growth
const MAX_IN_MEMORY_SESSIONS = 50;
let sessions: ChatSession[] = [];
let currentSessionId: string | null = null;

class ChatApiService {
  private async ensureSession(): Promise<ChatSession> {
    let session = await this.getCurrentSession();
    if (!session) {
      session = await this.createSession();
    }
    return session;
  }

  async addSystemMessage(
    content: string,
    threadId?: string,
    handlerType?: HandlerType,
  ): Promise<{ session: ChatSession; message: ChatMessage }> {
    let session = await this.getCurrentSession();
    if (!session) {
      session = await this.createSession();
    }

    const systemMessage: ChatMessage = {
      id: this.generateId(),
      content: content,
      sender: 'assistant',
      timestamp: new Date(),
    };

    // Build a new session object so TanStack Query detects the change.
    const updated: ChatSession = {
      ...session,
      messages: [...session.messages, systemMessage],
      updatedAt: new Date(),
      // Store the summarisation thread_id separately; clear the chat
      // thread_id so the next chat-stream starts a new conversation.
      ...(threadId ? { summaryThreadId: threadId, threadId: null } : {}),
      ...(handlerType ? { handlerType } : {}),
    };

    this.updateSessionInStorage(updated);

    return { session: updated, message: systemMessage };
  }

  /**
   * Update session in storage
   */
  private updateSessionInStorage(session: ChatSession): void {
    const index = sessions.findIndex((s) => s.id === session.id);
    if (index >= 0) {
      sessions[index] = session;
    }
  }

  /**
   * Generate generic id
   */
  private generateId(): string {
    return crypto.randomUUID();
  }

  async getSessions(): Promise<ChatSession[]> {
    try {
      const apiThreads = await fetcher<ApiThread[]>('/api/v1/chat/threads');

      const apiSessions: ChatSession[] = apiThreads.map((thread) => ({
        id: thread.langgraph_thread_id,
        title: thread.title || 'New Chat',
        messages: [],
        createdAt: new Date(thread.created_at),
        updatedAt: new Date(thread.updated_at),
        isActive: thread.is_active,
        threadId: thread.langgraph_thread_id,
      }));

      // Find the current in-memory session to preserve its messages
      const currentInMemorySession = currentSessionId
        ? sessions.find((s) => s.id === currentSessionId)
        : null;

      if (currentInMemorySession) {
        if (currentInMemorySession.threadId) {
          // Merge ALL runtime fields into the matching API session so nothing is lost
          // (messages, insightsContext, threadId, etc.) — only override server-authoritative
          // metadata such as title/timestamps from the API response.
          const apiIdx = apiSessions.findIndex((s) => s.id === currentInMemorySession.threadId);
          if (apiIdx >= 0) {
            const apiSession = apiSessions[apiIdx];
            apiSessions[apiIdx] = {
              ...currentInMemorySession,
              id: apiSession.id,                                      // langgraph_thread_id
              threadId: apiSession.id,                                // keep in sync
              title: apiSession.title || currentInMemorySession.title,
              createdAt: apiSession.createdAt,
              updatedAt: apiSession.updatedAt,
              isActive: apiSession.isActive,
            };
            // Keep currentSessionId aligned with langgraph_thread_id
            currentSessionId = currentInMemorySession.threadId;
          } else {
            // threadId is set but not listed under /api/v1/chat/threads — e.g. a
            // Robin Chat - Experimental thread, which lives in a different backend
            // thread space than the Nav "AI Chat" threads this list returns.
            // Preserve it as-is instead of silently dropping it (previously this
            // branch did nothing, so the session vanished from `sessions` and the
            // next message would look like a fresh conversation — thread_id reset
            // to undefined even though the backend still had the real thread open).
            apiSessions.unshift(currentInMemorySession);
          }
        } else {
          // New session not yet saved to API — prepend it
          apiSessions.unshift(currentInMemorySession);
        }
      }

      sessions = apiSessions;
      if (sessions.length > MAX_IN_MEMORY_SESSIONS) {
        sessions = sessions.slice(0, MAX_IN_MEMORY_SESSIONS);
      }
      return apiSessions;
    } catch {
      // Fallback to in-memory sessions if API call fails
      return sessions;
    }
  }

  async getCurrentSession(): Promise<ChatSession | null> {
    if (!currentSessionId) return null;
    return sessions.find((s) => s.id === currentSessionId) || null;
  }

  // Persists the right-panel context (workflow_id + checkpoint_id) set before any session exists,
  // so the first created session inherits it without needing a separate flush.
  private pendingInsightsContext: { workflowId: string; checkpointId: string } | null = null;

  setPendingInsightsContext(workflowId: string, checkpointId: string): void {
    this.pendingInsightsContext = { workflowId, checkpointId };
  }

  getPendingInsightsContext(): { workflowId: string; checkpointId: string } | null {
    return this.pendingInsightsContext;
  }

  async createSession(): Promise<ChatSession> {
    const session: ChatSession = {
      id: this.generateId(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      insightsContext: this.pendingInsightsContext
        ? { workflowId: this.pendingInsightsContext.workflowId, checkpointId: this.pendingInsightsContext.checkpointId }
        : undefined,
    };

    sessions = sessions.filter((s) => s.id !== session.id);
    sessions.unshift(session);
    if (sessions.length > MAX_IN_MEMORY_SESSIONS) {
      sessions = sessions.slice(0, MAX_IN_MEMORY_SESSIONS);
    }
    currentSessionId = session.id;
    return session;
  }

  async switchSession(sessionId: string): Promise<ChatSession> {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) {
      throw new Error('Session not found');
    }

    // Fetch messages from API when loading a historical session (threadId set but no messages loaded)
    if (session.threadId && session.messages.length === 0) {
      try {
        const apiMessages = await fetcher<ApiThreadMessage[]>(
          `/api/v1/chat/threads/${session.threadId}/messages`,
        );
        const messages: ChatMessage[] = apiMessages.map((m) => ({
          id: m.id || this.generateId(),
          sender: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          timestamp: new Date(),
        }));
        const populated = { ...session, messages };
        this.updateSessionInStorage(populated);
        currentSessionId = sessionId;
        return populated;
      } catch {
        // Fall through with empty messages if message fetch fails
      }
    }

    currentSessionId = sessionId;
    return session;
  }

  /** POST /api/v1/chat/threads/{thread_id}/messages/{message_id}/feedback */
  async submitFeedback(
    threadId: string,
    rating: 'positive' | 'negative',
    options?: { comment?: string; messageId?: string; agentId?: string },
  ): Promise<void> {
    const messageId = options?.messageId;
    if (!messageId) return;
    await post(`/api/v1/chat/threads/${threadId}/messages/${messageId}/feedback`, {
      rating,
      comment: options?.comment ?? null,
    });
  }

  async updateSessionTitle(sessionId: string, title: string): Promise<ChatSession> {
    const session = sessions.find((s) => s.id === sessionId);

    if (session?.threadId) {
      await patch(`/api/v1/chat/threads/${session.threadId}`, { title });
    }

    if (session) {
      session.title = title;
      session.updatedAt = new Date();
      this.updateSessionInStorage(session);

      return session;
    }

    return { id: sessionId, title, messages: [], createdAt: new Date(), updatedAt: new Date(), isActive: false };
  }

  /** Sync an externally-updated session into the service internal state (used by streaming hook). */
  syncSession(session: ChatSession): void {
    const index = sessions.findIndex((s) => s.id === session.id);
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.unshift(session);
      currentSessionId = session.id;
    }
  }

  /** Public wrapper around ensureSession for the streaming hook. */
  async getOrCreateSession(): Promise<ChatSession> {
    return this.ensureSession();
  }

  /** Fetch the latest checkpoint_id for an Insights Hub workflow. */
  async resolveCheckpointId(workflowId: string): Promise<string | undefined> {
    try {
      const dto = await fetcher<{ checkpoint_id?: string | null }>(
        `/api/v1/insights-hub/${encodeURIComponent(workflowId)}`,
      );
      return dto.checkpoint_id ?? undefined;
    } catch {
      return undefined;
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    const session = sessions.find((s) => s.id === sessionId);

    if (session?.threadId) {
      await del(`/api/v1/chat/threads/${session.threadId}`);
    }

    const index = sessions.findIndex((s) => s.id === sessionId);
    if (index >= 0) {
      sessions.splice(index, 1);
    }

    if (currentSessionId === sessionId) {
      currentSessionId = sessions.length > 0 ? sessions[0].id : null;
    }
  }
}

export const chatApiService = new ChatApiService();

// =============================================================================
// SSE Streaming — /api/v1/chat/chat-stream & /api/v1/chat/resume-stream
// =============================================================================

const BASE_URL = environment.VITE_APP_API_BASE_URL;

function tryParseJsonDetail(body: string): string {
  try {
    const json = JSON.parse(body);
    return typeof json.detail === 'string' ? json.detail : '';
  } catch {
    return '';
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const [token, graphToken] = await Promise.all([getAccessToken(), getGraphToken()]);

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (graphToken) headers['X-MS-Graph-Token'] = graphToken;

  return headers;
}

function dispatchNavEvent(event: NavStreamEvent, handlers: NavStreamEventHandlers): void {
  // Robin Chat - Experimental sends 'tool' events (tool call lifecycle) that
  // aren't part of NavStreamEvent's type union; no handler surfaces them in
  // the UI yet, so drop silently instead of logging a misleading "unknown"
  // warning for an expected, documented type. Checked before the switch so
  // TS's exhaustive narrowing on the real union isn't disturbed.
  if ((event as { type: string }).type === 'tool') return;

  switch (event.type) {
    case 'start':     handlers.onStart?.(event);     break;
    case 'state':     handlers.onState?.(event);     break;
    case 'reasoning': handlers.onReasoning?.(event); break;
    case 'content':   handlers.onContent?.(event);   break;
    case 'widget':    handlers.onWidget?.(event);    break;
    case 'navigate':  handlers.onNavigate?.(event);  break;
    case 'display':          handlers.onDisplay?.(event);         break;
    case 'content_summary':  handlers.onContentSummary?.(event);  break;
    case 'thread_summary':   handlers.onThreadSummary?.(event);   break;
    case 'error':            handlers.onError?.(event);           break;
    case 'done':             handlers.onDone?.(event);            break;
    case 'notice':    handlers.onNotice?.(event);    break;
    case 'suggested_actions': handlers.onSuggestedActions?.(event); break;
    case 'insights_context':  handlers.onInsightsContext?.(event);  break;
    case 'report':             handlers.onReport?.(event);            break;
    default:
      console.warn('Unknown SSE event type:', (event as { type: string }).type);
  }
}

interface SSEParseState {
  eventType: string;
  data: string;
}

function tryDispatchSSEEvent(state: SSEParseState, handlers: NavStreamEventHandlers): SSEParseState {
  if (!state.data) return state;
  try {
    const parsed = JSON.parse(state.data);
    if (!parsed.type && state.eventType) parsed.type = state.eventType;
    dispatchNavEvent(parsed as NavStreamEvent, handlers);
  } catch (err) {
    console.error('Failed to parse SSE data:', state.data, err);
  }
  return { eventType: '', data: '' };
}

function processSSELine(line: string, state: SSEParseState): SSEParseState {
  if (line.startsWith('event:')) return { ...state, eventType: line.slice(6).trim() };
  if (line.startsWith('data:')) {
    const value = line.slice(5).trim();
    return { ...state, data: state.data ? state.data + '\n' + value : value };
  }
  return state;
}

async function parseNavSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: NavStreamEventHandlers,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  let state: SSEParseState = { eventType: '', data: '' };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line === '') {
          state = tryDispatchSSEEvent(state, handlers);
        } else {
          state = processSSELine(line, state);
        }
      }
    }

    tryDispatchSSEEvent(state, handlers);
  } finally {
    reader.cancel().catch(() => {});
  }
}

export async function streamChat(
  request: NavChatStreamRequest,
  handlers: NavStreamEventHandlers,
  signal?: AbortSignal,
  endpoint = '/api/v1/chat/chat-stream',
): Promise<void> {
  const url = `${BASE_URL}${endpoint}`;
  try {
    const headers = await getAuthHeaders();
    headers['Accept'] = 'text/event-stream';

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      const parsed = detail ? tryParseJsonDetail(detail) : '';
      throw new Error(parsed || `Chat request failed (${response.status})`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    await parseNavSSEStream(reader, handlers);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;
    console.error('[streamChat] error', error);
    if (error instanceof Error) {
      handlers.onStreamError?.(error);
    } else {
      handlers.onStreamError?.(new Error('Unknown error during streaming'));
    }
  }
}

export async function resumeNavStream(
  request: NavResumeStreamRequest,
  handlers: NavStreamEventHandlers,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    headers['Accept'] = 'text/event-stream';

    const response = await fetch(`${BASE_URL}/api/v1/chat/resume-stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      const parsed = detail ? tryParseJsonDetail(detail) : '';
      throw new Error(parsed || `Chat request failed (${response.status})`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    await parseNavSSEStream(reader, handlers);
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') return;
      handlers.onStreamError?.(error);
    } else {
      handlers.onStreamError?.(new Error('Unknown error during resume'));
    }
  }
}

// =============================================================================
// Cancel & restore-checkpoint — no equivalent exists under /api/v1/chat/*, so
// these call the "Robin Chat - Experimental" backend (/api/v1/robin-chat/*)
// even though chat-stream/resume-stream stay on /api/v1/chat/*. Best-effort:
// the thread_id came from a /chat/chat-stream session, not a robin-chat one,
// so the backend may not recognize it (different agent/thread namespace) —
// callers should treat failures here as non-fatal.
// =============================================================================

export interface CancelStreamResponse {
  success: boolean;
  message: string;
}

export interface RestoreCheckpointResponse {
  success: boolean;
  message: string;
  restored_checkpoint_id?: string;
}

export async function cancelStream(threadId: string, runId: string): Promise<CancelStreamResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${BASE_URL}/api/v1/robin-chat/cancel`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ thread_id: threadId, run_id: runId }),
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

  return response.json();
}

export async function restoreCheckpoint(threadId: string, checkpointId: string): Promise<RestoreCheckpointResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${BASE_URL}/api/v1/robin-chat/restore`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ thread_id: threadId, checkpoint_id: checkpointId }),
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

  return response.json();
}
